import { CRS, LatLng, LatLngBounds, Point } from 'leaflet'

import {
  MAP_PROJECT_ZOOM_FLOOR_MAX,
  MAP_PROJECT_ZOOM_FLOOR_STEPS
} from 'src/constants/map'

/**
 * Where a map puts its view and what it keeps it inside, derived from the map's own geometry.
 *
 * Every function here takes numbers and a projection, not a map, so that they can be read and
 * tested without one. The limits are used in `project_map.tsx`, the centre of a fit in
 * `map.tsx`.
 */

/**
 * The lowest zoom a project map allows, from the zoom its bounding box fits at.
 *
 * Two levels below the fit, capped at both ends: never below the world's own level 0, and never
 * deeper than the maximum, which keeps the bound off the map's upper end. A project whose box
 * still fits its container at that end fits exactly there, so without the cap the bound would
 * move whenever the tile layer's range moves. How small that is depends on the container, not on
 * a tile.
 */
export const zoomFloorOf = (fittedZoom: number): number => Math.min(
  Math.max(0, fittedZoom - MAP_PROJECT_ZOOM_FLOOR_STEPS),
  MAP_PROJECT_ZOOM_FLOOR_MAX
)

/**
 * The section a map of this size shows around a centre at a given zoom, widened by `padding` of
 * its own extent per side.
 *
 * Computed through the projection rather than by padding a `LatLngBounds`: that pads in degrees,
 * and a degree of latitude covers fewer pixels the further the section reaches from the equator,
 * so a section grown in degrees is not the section the map shows.
 *
 * Leaflet's pixel space grows southwards, which is why the southern corner takes the larger `y`.
 */
export const boundsAround = (
  center: LatLng,
  size: Point,
  zoom: number,
  padding: number,
  crs: CRS = CRS.EPSG3857
): LatLngBounds => {
  const half = size.multiplyBy(0.5 + padding)
  const middle = crs.latLngToPoint(center, zoom)

  return new LatLngBounds(
    crs.pointToLatLng(new Point(middle.x - half.x, middle.y + half.y), zoom),
    crs.pointToLatLng(new Point(middle.x + half.x, middle.y - half.y), zoom)
  )
}

/**
 * The centre a box ends up on when a map of this size is fitted to it.
 *
 * The middle of the box in the projection, not the middle of its degrees: `LatLngBounds.getCenter`
 * averages latitudes, and in Web Mercator that is a different point from the one halfway down the
 * screen. Leaflet's own `fitBounds` takes the projected middle, and a view set beside it has to
 * take the same one or the box sits off centre.
 *
 * Padding that differs between the two corners moves the centre by half the difference, so that
 * the box ends up in the middle of what is left over. That is how the overview map keeps its
 * projects out from under the controls floating over the top of it. It is measured in pixels, and
 * that is the only reason the zoom is a parameter: without padding the two conversions cancel and
 * the answer is the same on every level.
 *
 * The zoom is the caller's, so that a level clamped to what the instance allows and the centre
 * belonging to it can never come apart.
 */
export const fitCenterOf = (
  bounds: LatLngBounds,
  zoom: number,
  paddingTopLeft: Point = new Point(0, 0),
  paddingBottomRight: Point = new Point(0, 0),
  crs: CRS = CRS.EPSG3857
): LatLng => {
  const southWest = crs.latLngToPoint(bounds.getSouthWest(), zoom)
  const northEast = crs.latLngToPoint(bounds.getNorthEast(), zoom)
  const offset = paddingBottomRight.subtract(paddingTopLeft).divideBy(2)

  return crs.pointToLatLng(southWest.add(northEast).divideBy(2).add(offset), zoom)
}

/**
 * How much of the room something takes at the top of a map a fit can actually keep free.
 *
 * A control floating over the top of the map swallows what is drawn underneath it, so a fit
 * would rather put its box below it. It can only do that with room to spare: the box is placed
 * in the middle of what the padding leaves, and pushing it down by more than half of what is
 * left over takes it out at the bottom. Where the box nearly fills the map, the answer is
 * nothing, and the fit stays even on all sides — a box out of the top is worse than one under a
 * button.
 *
 * Counted at the zoom the fit lands on, because that is the only level at which the box has a
 * size in pixels.
 */
export const clearanceFor = (
  bounds: LatLngBounds,
  size: Point,
  zoom: number,
  padding: number,
  covered: number,
  crs: CRS = CRS.EPSG3857
): number => {
  const north = crs.latLngToPoint(bounds.getNorthEast(), zoom).y
  const south = crs.latLngToPoint(bounds.getSouthWest(), zoom).y
  const left = size.y - 2 * padding - Math.abs(south - north)

  return Math.max(0, Math.min(covered, left))
}

/**
 * How far down a map something laid over its top edge reaches, in pixels.
 *
 * Nothing, where that something has no height. A group of controls positioned a margin below the
 * top edge still has a bottom edge a margin down when it holds nothing at all, and taking that
 * bottom edge at face value would keep a margin free with nothing in it to keep free.
 *
 * Negative never: an element that sits above the map covers none of it.
 */
export const coverageAtTop = (
  element: { top: number; bottom: number },
  containerTop: number
): number => {
  if (element.bottom - element.top <= 0) {
    return 0
  }

  return Math.max(0, Math.round(element.bottom - containerTop))
}
